import React, { useState, useMemo } from 'react';
import { Box } from '@mui/material';
import RangeSlider from '../../../components/RangeSlider/RangeSlider';
import DecimalTextField from '../DecimalTextField';
import { resolveDecimalFieldPolicy } from '../../utils/decimalInput';

export interface SliderWithTextFieldProps {
    /** Nome del campo per il form */
    name: string;
    /** Valore corrente */
    value: number;
    /** Valore minimo dinamico */
    min: number;
    /** Valore massimo */
    max: number;
    /** Step per lo slider */
    step?: number;
    /** Unità di misura da mostrare */
    unit?: string;
    /** Callback quando il valore cambia */
    onChange: (value: number) => void;
    /** Se il componente è disabilitato */
    disabled?: boolean;
    /** Precisione decimali per il display (default: 2) */
    decimalPrecision?: number;
    /** Larghezza del TextField (default: '60px') */
    textFieldWidth?: string;
    /** Testo digitato che non è (ancora) un numero utilizzabile */
    onInvalidText?: (raw: string) => void;
    /** Un cambio riporta il testo al valore corrente, se il campo non è in modifica */
    resetKey?: string | number;
}

const SLIDER_DECIMAL_POLICY = resolveDecimalFieldPolicy({ renderAs: 'slider' }, 'slider');

/**
 * Componente che combina uno slider con un TextField editabile per input numerici.
 * Supporta sincronizzazione bidirezionale: slider ↔ TextField
 */

const SliderWithTextField: React.FC<SliderWithTextFieldProps> = ({
    name,
    value,
    min,
    max,
    step = 0.1,
    unit = '',
    onChange,
    disabled = false,
    decimalPrecision = 2,
    textFieldWidth = '60px',
    onInvalidText,
    resetKey,
}) => {
    // Un movimento del thumb sostituisce il testo non valido ancora in modifica
    const [thumbMoves, setThumbMoves] = useState(0);

    // Valore corrente vincolato ai limiti
    const currentValue = useMemo(() => {
        return Math.max(Math.min(value, max), min);
    }, [value, min, max]);

    // Handler per il cambiamento dello slider
    const handleSliderChange = (event: Event, newValue: number | number[]) => {
        if (disabled) return;

        const numericValue = Array.isArray(newValue) ? newValue[0] : newValue;
        const constrainedValue = Math.max(numericValue, min);
        setThumbMoves(moves => moves + 1);
        onChange(constrainedValue);
    };

    // Apply min/max constraints only. Step snap intentionally NOT applied to the
    // typed value: the numeric input is the authoritative user value. The slider
    // thumb keeps 5cm snap via the `step` prop forwarded to MUI Slider.
    const handleTextValueChange = (typedValue: number | null) => {
        if (disabled || typedValue === null) return;
        onChange(Math.min(Math.max(typedValue, min), max));
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Slider */}
            <RangeSlider key={`${name}-${min}`} name={name} value={currentValue} min={min} max={max} step={step} fullWidth={true} onChange={handleSliderChange} />

            {/* Labels e TextField */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                }}>
                {/* Label minimo */}
                <span>
                    {min.toFixed(decimalPrecision)}
                    {unit}
                </span>

                {/* TextField centrale con unità */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        '& .MuiOutlinedInput-root': {
                            fontSize: '0.75rem',
                            height: '24px',
                            '& fieldset': {
                                borderColor: 'rgba(0, 0, 0, 0.23)',
                            },
                            '&:hover fieldset': {
                                borderColor: 'rgba(0, 0, 0, 0.87)',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#00424E',
                            },
                            '&.Mui-disabled': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        },
                        '& .MuiOutlinedInput-input': {
                            padding: '2px 8px',
                            textAlign: 'center',
                            fontSize: '0.75rem',
                        },
                    }}>
                    <DecimalTextField
                        variant="outlined"
                        size="small"
                        value={currentValue}
                        policy={SLIDER_DECIMAL_POLICY}
                        emptyPolicy="restoreStore"
                        formatBlurred={storedValue => Number(storedValue).toFixed(decimalPrecision)}
                        onValueChange={handleTextValueChange}
                        onInvalidText={onInvalidText}
                        resetKey={`${resetKey ?? ''}:${thumbMoves}`}
                        disabled={disabled}
                        inputProps={{
                            style: { width: textFieldWidth },
                            'aria-label': `${name} value input`,
                        }}
                    />
                    <span style={{ fontSize: '0.75rem' }}>{unit}</span>
                </Box>

                {/* Label massimo */}
                <span>
                    {max.toFixed(decimalPrecision)}
                    {unit}
                </span>
            </Box>
        </Box>
    );
};

export default SliderWithTextField;
